import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Loader2,
    Inbox,
    Send,
    Reply,
    Forward,
    Paperclip,
    RefreshCw,
    ChevronDown,
    Search,
} from "lucide-react";
import { listEmailMessages, getEmailMessage } from "../../lib/api";
import { fmtRelDate } from "./utils";
import MessageView from "./MessageView";

const FOLDERS = [
    { key: "INBOX", label: "Inbox", Icon: Inbox },
    { key: "Sent", label: "Sent", Icon: Send },
];

export default function MailboxLayout({ account, accounts, isAllInboxes, folder, onFolderChange, onCompose }) {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [selectedUid, setSelectedUid] = useState(null);
    // For "All inboxes" mode, a selected message ALSO needs to remember
    // which account it came from so we can fetch / reply correctly.
    const [selectedAccountId, setSelectedAccountId] = useState(null);
    const [searchInput, setSearchInput] = useState("");
    const [activeQuery, setActiveQuery] = useState("");
    // iter-109: client-controlled message limit. Initial load is 200
    // (per account on All Inboxes) and grows by 100 each tap of
    // "Load 100 more". Resets on account / folder / search change.
    // We also track `hasMore` so the button doesn't get stuck visible
    // when the mailbox has fewer messages than the requested limit
    // (the old `messages.length >= limit` check broke this in
    // All Inboxes mode where messages.length is summed across
    // accounts but limit is per-account).
    const INITIAL_LIMIT = 200;
    const [limit, setLimit] = useState(INITIAL_LIMIT);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    const load = async (silent = false, queryOverride = null, limitOverride = null) => {
        if (!account) return;
        if (silent) setRefreshing(true); else setLoading(true);
        try {
            const q = queryOverride === null ? activeQuery : queryOverride;
            const lim = limitOverride ?? limit;
            if (isAllInboxes) {
                // Fan-out: fetch every account in parallel, merge by date desc.
                // Failures per-account are non-fatal — partial result is fine.
                // `hasMore` is TRUE if ANY single account returned a full
                // page — that account likely has more to give on the
                // next click. If every account returned less than `lim`,
                // there's nothing left anywhere.
                const results = await Promise.all(
                    (accounts || []).map(async (acc) => {
                        try {
                            const r = await listEmailMessages(acc.id, folder, lim, q);
                            return (r?.messages || []).map((m) => ({
                                ...m,
                                _account_id: acc.id,
                                _account_email: acc.email_address,
                                _account_provider: acc.provider,
                            }));
                        } catch {
                            return [];
                        }
                    }),
                );
                const perAccountFull = results.some((arr) => (arr?.length || 0) >= lim);
                const merged = results.flat().sort((a, b) =>
                    (b.date || "").localeCompare(a.date || ""),
                );
                setMessages(merged);
                setHasMore(perAccountFull);
            } else {
                const r = await listEmailMessages(account.id, folder, lim, q);
                const rows = r?.messages || [];
                setMessages(rows);
                setHasMore(rows.length >= lim);
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't load mailbox");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const handleLoadMore = async () => {
        if (loadingMore || !hasMore) return;
        const next = limit + 100;
        setLimit(next);
        setLoadingMore(true);
        try {
            await load(true, null, next);
        } finally {
            setLoadingMore(false);
        }
    };

    // Re-fetch whenever the account or folder changes. Reset search when folder/account changes.
    useEffect(() => {
        setSelectedUid(null);
        setSelectedAccountId(null);
        setSearchInput("");
        setActiveQuery("");
        setLimit(INITIAL_LIMIT);
        setHasMore(true);
        load(false, "", INITIAL_LIMIT);
        /* eslint-disable-next-line */
    }, [account?.id, folder, isAllInboxes]);

    const onSearchSubmit = (e) => {
        e?.preventDefault?.();
        const q = searchInput.trim();
        setActiveQuery(q);
        setSelectedUid(null);
        setLimit(INITIAL_LIMIT);
        setHasMore(true);
        load(false, q, INITIAL_LIMIT);
    };
    const onClearSearch = () => {
        setSearchInput("");
        setActiveQuery("");
        setSelectedUid(null);
        setLimit(INITIAL_LIMIT);
        setHasMore(true);
        load(false, "", INITIAL_LIMIT);
    };

    const selectedMessage = selectedUid;

    return (
        <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] lg:grid-cols-[180px_1fr_2fr] gap-3 min-h-[60vh]">
            {/* Folder sidebar */}
            <aside
                data-testid="email-folder-list"
                className={`rounded-sm border border-[#2A3655] bg-[#0A1128]/60 p-2 self-start ${selectedMessage ? "hidden sm:block" : ""}`}
            >
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold px-2 py-1.5">
                    Folders
                </div>
                {FOLDERS.map((f) => {
                    const F = f.Icon;
                    const active = folder === f.key;
                    return (
                        <button
                            key={f.key}
                            onClick={() => onFolderChange(f.key)}
                            data-testid={`email-folder-${f.key.toLowerCase()}`}
                            className={`w-full flex items-center gap-2 px-2 py-2 rounded-sm text-sm transition-colors ${
                                active
                                    ? "bg-[#00A3FF]/15 text-[#00A3FF] font-bold"
                                    : "text-slate-300 hover:bg-[#1C253C]"
                            }`}
                        >
                            <F className="w-4 h-4" />
                            {f.label}
                        </button>
                    );
                })}
            </aside>

            {/* Message list */}
            <section
                data-testid="email-message-list"
                className={`rounded-sm border border-[#2A3655] bg-[#0A1128]/60 overflow-hidden min-h-[50vh] flex flex-col ${selectedMessage ? "hidden lg:flex" : "flex"}`}
            >
                <div className="flex items-center justify-between px-3 py-2 border-b border-[#2A3655]">
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-200">
                        {folder} <span className="text-slate-500">· {messages.length}</span>
                        {activeQuery && (
                            <span className="ml-2 text-[10px] normal-case text-[#00A3FF]">
                                matches: "{activeQuery}"
                            </span>
                        )}
                    </div>
                    <button
                        onClick={() => load(true)}
                        disabled={refreshing}
                        data-testid="email-refresh-btn"
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-slate-400 hover:text-[#00A3FF] px-2 py-1 rounded-sm border border-[#2A3655] hover:border-[#00A3FF]"
                    >
                        {refreshing ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                            <RefreshCw className="w-3 h-3" />
                        )}
                        Refresh
                    </button>
                </div>
                <form
                    onSubmit={onSearchSubmit}
                    className="flex items-center gap-2 px-3 py-2 border-b border-[#2A3655] bg-[#0A1128]/40"
                    data-testid="email-search-form"
                >
                    <Search className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                    <input
                        type="text"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder="Search subject, sender, body…"
                        data-testid="email-search-input"
                        className="flex-1 min-w-0 bg-transparent text-sm text-slate-200 placeholder:text-slate-600 outline-none"
                    />
                    {activeQuery ? (
                        <button
                            type="button"
                            onClick={onClearSearch}
                            data-testid="email-search-clear-btn"
                            className="text-[10px] uppercase tracking-wider font-bold text-slate-500 hover:text-rose-400 px-1.5"
                        >
                            Clear
                        </button>
                    ) : null}
                    <button
                        type="submit"
                        data-testid="email-search-submit-btn"
                        className="text-[10px] uppercase tracking-wider font-bold text-[#00A3FF] hover:text-white px-2 py-1 rounded-sm border border-[#2A3655] hover:border-[#00A3FF]"
                    >
                        Search
                    </button>
                </form>
                <div className="overflow-y-auto flex-1 min-h-[40vh] max-h-[calc(100dvh-280px)]">
                    {loading ? (
                        <div className="flex items-center justify-center py-10 text-slate-500 text-sm">
                            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
                        </div>
                    ) : messages.length === 0 ? (
                        <div className="text-center py-10 text-slate-500 text-sm italic">
                            No messages in {folder}.
                        </div>
                    ) : (
                        messages.map((m) => {
                            const rowAccountId = m._account_id || account.id;
                            const quickAction = async (mode, ev) => {
                                ev.stopPropagation();
                                ev.preventDefault();
                                try {
                                    const full = await getEmailMessage(
                                        rowAccountId, m.uid, folder, false,
                                    );
                                    onCompose(mode, { ...full, _account_id: rowAccountId });
                                } catch (err) {
                                    toast.error(err?.response?.data?.detail || "Couldn't open message");
                                }
                            };
                            return (
                            <div
                                key={`${rowAccountId}_${m.uid}`}
                                role="button"
                                tabIndex={0}
                                onClick={() => {
                                    setSelectedUid(m.uid);
                                    setSelectedAccountId(rowAccountId);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        setSelectedUid(m.uid);
                                        setSelectedAccountId(rowAccountId);
                                    }
                                }}
                                data-testid={`email-msg-row-${m.uid}`}
                                className={`group w-full text-left px-3 py-2.5 border-b border-[#2A3655]/40 hover:bg-[#1C253C]/60 transition-colors cursor-pointer relative ${
                                    selectedUid === m.uid ? "bg-[#00A3FF]/10" : ""
                                } ${!m.seen ? "border-l-2 border-l-[#00A3FF]" : ""}`}
                            >
                                <div className="flex items-baseline gap-2">
                                    <span className={`text-sm truncate flex-1 min-w-0 ${m.seen ? "text-slate-300" : "text-white font-bold"}`}>
                                        {folder === "Sent"
                                            ? (m.to[0] || "(no recipient)")
                                            : (m.from_name || m.from_addr || "(unknown)")}
                                    </span>
                                    <span className="text-[10px] text-slate-500 shrink-0">
                                        {fmtRelDate(m.date)}
                                    </span>
                                </div>
                                <div className={`text-xs truncate ${m.seen ? "text-slate-400" : "text-slate-200 font-semibold"}`}>
                                    {m.subject}
                                </div>
                                <div className="text-[10px] text-slate-500 truncate mt-0.5 flex items-center gap-1">
                                    {m.has_attachments && <Paperclip className="w-2.5 h-2.5" />}
                                    {isAllInboxes && m._account_email && (
                                        <span className="text-[#00A3FF]/80 font-bold">{m._account_email}</span>
                                    )}
                                    <span className="truncate">{m.snippet}</span>
                                </div>
                                {/* Quick Reply / Forward — pinned to the
                                    BOTTOM-right so the date (top-right)
                                    stays visible on every row, even on
                                    mobile where the buttons are always
                                    rendered. */}
                                <div className="absolute bottom-1.5 right-1.5 flex gap-0.5 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
                                    <button
                                        type="button"
                                        onClick={(ev) => quickAction("reply", ev)}
                                        data-testid={`email-msg-quick-reply-${m.uid}`}
                                        title="Reply"
                                        aria-label="Reply"
                                        className="p-1 rounded-sm bg-[#0A1128]/80 border border-[#2A3655] hover:border-[#00A3FF] hover:text-[#00A3FF] text-slate-300"
                                    >
                                        <Reply className="w-3 h-3" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={(ev) => quickAction("forward", ev)}
                                        data-testid={`email-msg-quick-forward-${m.uid}`}
                                        title="Forward"
                                        aria-label="Forward"
                                        className="p-1 rounded-sm bg-[#0A1128]/80 border border-[#2A3655] hover:border-[#00A3FF] hover:text-[#00A3FF] text-slate-300"
                                    >
                                        <Forward className="w-3 h-3" />
                                    </button>
                                </div>
                            </div>
                            );
                        })
                    )}
                    {/* iter-109: paginate inbox in 100-row chunks. The
                        button stays visible as long as the previous
                        fetch returned a full page — `hasMore` is set
                        by `load()` and is correct for both single-
                        account and All-Inboxes views. */}
                    {!loading && messages.length > 0 && hasMore && (
                        <div className="px-4 py-3 border-t border-[#2A3655] bg-[#0A1128]/60 flex justify-center">
                            <button
                                type="button"
                                onClick={handleLoadMore}
                                disabled={loadingMore}
                                data-testid="email-load-more"
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-[#1C253C] hover:bg-[#223055] border border-[#2A3655] hover:border-[#00A3FF] text-[11px] sm:text-xs font-bold uppercase tracking-wider text-slate-200 hover:text-white transition-colors disabled:opacity-50"
                            >
                                {loadingMore ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin text-[#00A3FF]" />
                                ) : (
                                    <ChevronDown className="w-3.5 h-3.5 text-[#00A3FF]" />
                                )}
                                Load 100 more
                                <span className="text-slate-500 font-mono">({messages.length} shown)</span>
                            </button>
                        </div>
                    )}
                </div>
            </section>

            {/* Message view */}
            <section
                data-testid="email-message-view"
                className={`rounded-sm border border-[#2A3655] bg-[#0A1128]/60 overflow-hidden min-h-[50vh] ${selectedMessage ? "" : "hidden lg:block"}`}
            >
                {selectedMessage ? (
                    <MessageView
                        account={
                            isAllInboxes
                                ? (accounts || []).find((a) => a.id === selectedAccountId) || account
                                : account
                        }
                        folder={folder}
                        uid={selectedMessage}
                        onBack={() => { setSelectedUid(null); setSelectedAccountId(null); }}
                        onDeleted={() => { setSelectedUid(null); setSelectedAccountId(null); load(true); }}
                        onCompose={onCompose}
                    />
                ) : (
                    <div className="flex items-center justify-center py-20 text-slate-500 text-sm italic h-full">
                        Pick a message to read it.
                    </div>
                )}
            </section>
        </div>
    );
}
